import { useEffect, useRef, useState } from "react";
import { fmtMYT, fmtMYTDate } from "@/lib/myt";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import MemberAvatar from "@/components/MemberAvatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";

const ADDRESS_FIELDS = [
  ["recipient_name", "Full Recipient Name", "text", true],
  ["phone", "Phone Number", "text", true],
  ["email", "Email Address", "email", true],
  ["country", "Country", "text", true],
  ["state", "State / Province", "text", true],
  ["city", "City", "text", true],
  ["postal_code", "Postal / ZIP Code", "text", true],
  ["address_line1", "Address Line 1", "text", true],
  ["address_line2", "Address Line 2 (optional)", "text", false],
];

export default function MemberProfile() {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [address, setAddress] = useState(null);
  const [addressForm, setAddressForm] = useState(null);
  const [editingAddress, setEditingAddress] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressError, setAddressError] = useState("");
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const { logout } = useAuth();
  const [delOpen, setDelOpen] = useState(false);
  const [delForm, setDelForm] = useState({ password: "", confirm: "" });
  const [delBusy, setDelBusy] = useState(false);
  const [delError, setDelError] = useState("");
  const [delDone, setDelDone] = useState(null);
  const logoutTimer = useRef(null);

  useEffect(() => () => clearTimeout(logoutTimer.current), []);

  const submitDeletionRequest = async (e) => {
    e.preventDefault();
    setDelError("");
    if (delForm.confirm !== "DELETE") {
      setDelError("Type exactly: DELETE");
      return;
    }
    setDelBusy(true);
    try {
      const { data } = await api.post("/member/account/deletion-request", {
        password: delForm.password,
        confirmation: delForm.confirm,
      });
      setDelDone(data);
      logoutTimer.current = setTimeout(() => logout(), 6000);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        setDelError(formatApiError(err));
      } else if (status === 401) {
        setDelError("Your session has expired. Please log in again.");
      } else if (status === 429) {
        setDelError("Too many failed attempts. Please wait a few minutes and try again.");
      } else {
        setDelError("Network error — could not reach the server. Please check your connection and try again.");
      }
    } finally {
      setDelBusy(false);
    }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/member/avatar", fd);
      setProfile((p) => ({ ...p, avatar_url: data.avatar_url }));
      toast.success("Profile avatar updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAvatarBusy(false);
    }
  };

  const removeAvatar = async () => {
    setAvatarBusy(true);
    try {
      await api.delete("/member/avatar");
      setProfile((p) => ({ ...p, avatar_url: null }));
      toast.success("Profile avatar removed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAvatarBusy(false);
    }
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    setPwError("");
    setPwSuccess(false);
    if (pwForm.next !== pwForm.confirm) {
      setPwError("New passwords do not match");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwForm.current,
        new_password: pwForm.next,
        confirm_new_password: pwForm.confirm,
      });
      setPwSuccess(true);
      setPwForm({ current: "", next: "", confirm: "" });
      toast.success("Password updated successfully");
    } catch (err) {
      setPwError(formatApiError(err));
    } finally {
      setPwBusy(false);
    }
  };

  useEffect(() => {
    api
      .get("/member/profile")
      .then((res) => setProfile(res.data))
      .catch(() => setError("Unable to load profile."));
    api
      .get("/member/address")
      .then((res) => setAddress(res.data.address))
      .catch(() => {});
  }, []);

  const startEditAddress = () => {
    setAddressError("");
    setAddressForm(
      address || {
        recipient_name: profile?.user?.full_name || "",
        phone: profile?.user?.phone || "",
        email: profile?.user?.email || "",
        country: profile?.user?.country || "",
        state: "",
        city: "",
        postal_code: "",
        address_line1: "",
        address_line2: "",
      },
    );
    setEditingAddress(true);
  };

  const saveAddress = async (e) => {
    e.preventDefault();
    setSavingAddress(true);
    setAddressError("");
    try {
      const { data } = await api.put("/member/address", addressForm);
      setAddress(data.address);
      setEditingAddress(false);
      toast.success("Delivery address saved");
    } catch (err) {
      setAddressError(formatApiError(err));
    } finally {
      setSavingAddress(false);
    }
  };

  if (error) {
    return <p className="text-sm text-red-400" data-testid="member-profile-error">{error}</p>;
  }
  if (!profile) {
    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="member-profile-loading">
        <div className="space-y-2">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="card-lux p-6 space-y-4">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-3 w-40" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const { user, sponsor, rank, indicator_access, joined_at } = profile;
  const rows = [
    ["Member UID", user.uid || "—", true],
    ["Username", user.username, true],
    ["Full Name", user.full_name, false],
    ["Email", user.email, false],
    ["Current Rank", rank?.current ? (rank.current.level ? `${rank.current.name} (Level ${rank.current.level}/36)` : "New Trader") : "—", false],
    [
      "Indicator Access",
      indicator_access?.status === "active"
        ? `Active${indicator_access.expires_at ? ` until ${fmtMYTDate(indicator_access.expires_at)}` : ""}`
        : "Not Active",
      false,
    ],
    ["Account Status", user.status === "active" ? "Active" : "Suspended", false],
    ["Introducing Broker", sponsor ? `${sponsor.ib_name || "IB"} (${sponsor.referral_code})` : "—", false],
    ["Member Since", joined_at ? fmtMYTDate(joined_at) : "—", false],
  ];

  return (
    <div className="max-w-3xl space-y-6" data-testid="member-profile-page">
      <div>
        <p className="eyebrow-label mb-2">Account</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Profile</h1>
      </div>
      <div className="card-lux p-6 sm:p-8" data-testid="profile-avatar-card">
        <p className="eyebrow-label mb-5">Profile Avatar</p>
        <div className="flex items-center gap-6 flex-wrap">
          <MemberAvatar avatarUrl={profile.avatar_url} size={96} ring="border-[#D4AF37]/40" testId="profile-avatar-preview" />
          <div className="space-y-3">
            <p className="text-xs text-[#64748B] max-w-sm">
              JPG, PNG or WebP, max 5 MB. Your avatar appears on your profile and the Top 10 leaderboard.
            </p>
            <div className="flex gap-3 flex-wrap">
              <label className={`btn-outline-gold px-4 py-2 text-xs cursor-pointer ${avatarBusy ? "opacity-50 pointer-events-none" : ""}`} data-testid="avatar-upload-button">
                {avatarBusy ? "Uploading..." : profile.avatar_url ? "Replace Avatar" : "Upload Avatar"}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadAvatar} data-testid="avatar-file-input" />
              </label>
              {profile.avatar_url && (
                <button type="button" onClick={removeAvatar} disabled={avatarBusy} className="btn-outline-gold px-4 py-2 text-xs disabled:opacity-50" data-testid="avatar-remove-button">
                  Remove Avatar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card-lux divide-y divide-[#222530]">
        {rows.map(([label, value, mono]) => (
          <div key={label} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6 px-6 py-4">
            <p className="text-xs text-[#64748B] w-44 shrink-0 tracking-wide">{label}</p>
            <p
              className={`text-sm text-white break-all ${mono ? "font-mono tracking-wider" : ""}`}
              data-testid={`profile-${label.toLowerCase().replace(/[\s/]+/g, "-")}`}
            >
              {value}
            </p>
          </div>
        ))}
      </div>

      <div className="card-lux p-6 sm:p-8" data-testid="member-address-card">
        <div className="flex items-center justify-between mb-5">
          <p className="eyebrow-label">Delivery Address</p>
          {!editingAddress && (
            <button onClick={startEditAddress} className="btn-outline-gold px-4 py-1.5 text-xs" data-testid="address-edit-button">
              {address ? "Edit Address" : "Add Address"}
            </button>
          )}
        </div>
        {!editingAddress ? (
          address ? (
            <div className="text-sm text-[#94A3B8] leading-relaxed" data-testid="address-summary">
              <p className="text-white font-medium">{address.recipient_name}</p>
              <p>{address.address_line1}{address.address_line2 ? `, ${address.address_line2}` : ""}</p>
              <p>{address.city}, {address.state} {address.postal_code}</p>
              <p>{address.country}</p>
              <p className="mt-2 font-mono text-xs">{address.phone} · {address.email}</p>
            </div>
          ) : (
            <p className="text-sm text-[#64748B]" data-testid="address-empty">
              No delivery address saved. Add one to claim physical rewards from the Reward Mall.
            </p>
          )
        ) : (
          <form onSubmit={saveAddress} className="grid sm:grid-cols-2 gap-4" data-testid="address-form">
            {ADDRESS_FIELDS.map(([key, label, type, required]) => (
              <div key={key} className={key.startsWith("address_line") ? "sm:col-span-2" : ""}>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                  {label} {required && <span className="text-red-400">*</span>}
                </label>
                <input
                  type={type}
                  className="input-lux"
                  value={addressForm[key] || ""}
                  onChange={(e) => setAddressForm((f) => ({ ...f, [key]: e.target.value }))}
                  required={required}
                  data-testid={`address-${key.replace(/_/g, "-")}-input`}
                />
              </div>
            ))}
            {addressError && (
              <p className="sm:col-span-2 text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="address-error">
                {addressError}
              </p>
            )}
            <div className="sm:col-span-2 flex gap-3">
              <button type="submit" disabled={savingAddress} className="btn-gold px-8 py-2.5 text-sm disabled:opacity-60" data-testid="address-save-button">
                {savingAddress ? "Saving..." : address ? "Update Address" : "Save Address"}
              </button>
              <button type="button" onClick={() => setEditingAddress(false)} className="btn-outline-gold px-5 py-2.5 text-sm" data-testid="address-cancel-button">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="card-lux p-6 sm:p-8" data-testid="change-password-card">
        <p className="eyebrow-label mb-5">Change Password</p>
        <form onSubmit={submitPasswordChange} className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Current Password</label>
            <input type="password" className="input-lux" value={pwForm.current} onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))} required autoComplete="current-password" data-testid="change-password-current-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">New Password</label>
            <input type="password" className="input-lux" value={pwForm.next} onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))} required minLength={8} autoComplete="new-password" data-testid="change-password-new-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Confirm New Password</label>
            <input type="password" className="input-lux" value={pwForm.confirm} onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))} required minLength={8} autoComplete="new-password" data-testid="change-password-confirm-input" />
          </div>
          {pwError && (
            <p className="sm:col-span-3 text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="change-password-error">{pwError}</p>
          )}
          {pwSuccess && (
            <p className="sm:col-span-3 text-sm text-emerald-400 bg-emerald-400/10 border border-emerald-400/25 rounded-lg px-4 py-3" data-testid="change-password-success">
              Password updated successfully. Use your new password the next time you log in.
            </p>
          )}
          <div className="sm:col-span-3">
            <button type="submit" disabled={pwBusy} className="btn-gold px-8 py-2.5 text-sm disabled:opacity-60" data-testid="change-password-submit-button">
              {pwBusy ? "Updating..." : "Update Password"}
            </button>
          </div>
        </form>
      </div>
      <div className="card-lux p-6 sm:p-8 border border-red-500/25" data-testid="delete-account-card">
        <p className="eyebrow-label mb-5 !text-red-400">Danger Zone</p>
        {delDone ? (
          <div className="space-y-4" data-testid="delete-account-success">
            <p className="text-lg font-heading font-bold text-emerald-400">Request Received</p>
            <p className="text-sm text-[#94A3B8] leading-relaxed">
              {delDone.message || "Your account deletion request has been recorded and will be reviewed by an administrator."}
            </p>
            <p className="text-xs text-[#64748B] leading-relaxed">
              {delDone.warning || "Account deletion is permanent. Your COINS balance, rewards, entitlements, certificates and all product access will be lost and cannot be recovered."}
            </p>
            <p className="text-xs text-[#64748B]">You will be logged out automatically in a few seconds.</p>
            <button type="button" onClick={() => { clearTimeout(logoutTimer.current); logout(); }} className="btn-outline-gold px-6 py-2.5 text-sm" data-testid="delete-account-logout-button">
              Log Out Now
            </button>
          </div>
        ) : !delOpen ? (
          <div className="space-y-4">
            <p className="text-sm text-[#94A3B8] leading-relaxed" data-testid="delete-account-warning">
              Permanently request deletion of your PlusMaker account. This is <span className="text-red-400 font-medium">permanent</span> —
              your membership, COINS balance, rewards, certificates, entitlements and all product access may not be recoverable.
              Financial records required for accounting may be retained in anonymised form. Your account is not deleted immediately:
              an administrator reviews and processes every request.{" "}
              <a href="/deletion" className="text-[#D4AF37] hover:text-[#E5C158]" data-testid="delete-account-info-link">Read the full account deletion information</a>.
            </p>
            <button type="button" onClick={() => setDelOpen(true)} className="px-6 py-2.5 text-sm font-medium rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors" data-testid="delete-account-open-button">
              Delete My Account
            </button>
          </div>
        ) : (
          <form onSubmit={submitDeletionRequest} className="space-y-4" data-testid="delete-account-form">
            <p className="text-sm text-[#94A3B8] leading-relaxed">
              To confirm, enter your current password and type <span className="font-mono text-red-400">DELETE</span> exactly.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Current Password</label>
                <input type="password" className="input-lux" value={delForm.password} onChange={(e) => setDelForm((f) => ({ ...f, password: e.target.value }))} required autoComplete="current-password" data-testid="delete-account-password-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Type DELETE to confirm</label>
                <input type="text" className="input-lux font-mono" value={delForm.confirm} onChange={(e) => setDelForm((f) => ({ ...f, confirm: e.target.value }))} required placeholder="DELETE" autoComplete="off" data-testid="delete-account-confirm-input" />
              </div>
            </div>
            {delError && (
              <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="delete-account-error">{delError}</p>
            )}
            <div className="flex gap-3">
              <button type="submit" disabled={delBusy || delForm.confirm !== "DELETE"} className="px-8 py-2.5 text-sm font-medium rounded-lg bg-red-500/90 text-white hover:bg-red-500 transition-colors disabled:opacity-50" data-testid="delete-account-submit-button">
                {delBusy ? "Submitting..." : "Submit Deletion Request"}
              </button>
              <button type="button" onClick={() => { setDelOpen(false); setDelError(""); setDelForm({ password: "", confirm: "" }); }} className="btn-outline-gold px-5 py-2.5 text-sm" data-testid="delete-account-cancel-button">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
